import React, {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import moment from "moment";

import Loading from "@/components/Loading";
import Page from "@/components/Page";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import VLFCard from "@/components/Card";
import ViewField from "@/components/Input/ViewField";
import VLFModal from "@/components/Modal";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

import {MedicalAPI} from "@/api/medical";

import {ERROR_OPERATION, SUCCESSFUL_OPERATION} from "@/utils/constants";
import {isDateValid} from "@/utils/functions";

function InjuryPage(props) {
	const {handleNotification} = props;
	const navigate = useNavigate();
	const {recordId, id} = useParams();

	const [loading, setLoading] = useState(false);
	const [openHandleConfirm, setOpenHandleConfirm] = useState(false);
	const [injury, setInjury] = useState(null);
	const [playerData, setPlayerData] = useState(null);
	const [loadError, setLoadError] = useState(false);
	const isInjury = !injury?.recordType;
	const recordPath = "/medical/records/" + recordId;

	useEffect(() => {
		const loadInjury = async () => {
			setLoading(true);
			setLoadError(false);
			try {
				setInjury(await MedicalAPI.injuryGetById(recordId, id));
			} catch {
				setLoadError(true);
				handleNotification(ERROR_OPERATION, "error");
			} finally {
				setLoading(false);
			}
		};
		loadInjury();
		MedicalAPI.recordGetById(recordId)
			.then((record) => setPlayerData(record?.playerData || null))
			.catch(() => handleNotification(ERROR_OPERATION, "error"));
	}, [recordId, id]);

	const deleteInjury = async () => {
		setLoading(true);
		try {
			await MedicalAPI.injuryDelete(recordId, id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			navigate(recordPath);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		} finally {
			setOpenHandleConfirm(false);
		}
	};

	const date = (value) =>
		isDateValid(value) ? moment(value).format("DD/MM/YYYY") : "---";
	const field = (label, value) => ({value, label, size: "col-12 col-lg-6"});

	const configuration = [
		field("Inizio", date(injury?.startDate)),
		field("Diagnosi", injury?.diagnosis),
		...(isInjury
			? [
					field("Tipo di infortunio", injury?.injuryType),
					field("Parte del corpo", injury?.bodyPart),
					field("Causa", injury?.cause),
				]
			: []),
		field("Esame", injury?.exam),
		field("Terapia", injury?.therapy),
		field("Dimissione medica", date(injury?.medicalDischargeDate)),
		...(isInjury
			? [field("Rientro in campo", date(injury?.sportsDischargeDate))]
			: []),
		...(isDateValid(injury?.endDate)
			? [field("Fine", date(injury?.endDate))]
			: []),
		field("Note", injury?.notes),
	];

	const mainActions = [
		{
			icon: <ArrowBackIcon />,
			title: "Indietro",
			onClick: () => navigate(recordPath),
			color: "info",
		},
		{
			icon: <EditIcon />,
			title: "Modifica",
			onClick: () => navigate(recordPath + "/injuries/" + id + "/edit"),
		},
		{
			icon: <DeleteIcon />,
			title: "Elimina",
			onClick: () => setOpenHandleConfirm(true),
			color: "error",
			variant: "outlined",
		},
	];

	const breadcrumbsElements = [
		{label: "Cartelle", path: "/medical/records"},
		{label: "/"},
		{
			label: [playerData?.firstName, playerData?.lastName]
				.filter(Boolean)
				.join(" "),
			path: recordPath,
		},
		{label: "/"},
	];

	if (loadError)
		return (
			<Page>
				<div>Impossibile caricare l’infortunio.</div>
			</Page>
		);

	return (
		<Page>
			<Loading visible={loading} />
			<Breadcrumbs
				elements={breadcrumbsElements}
				current={injury ? (isInjury ? "Infortunio" : "Malattia") : ""}
				actions={<MainActions actions={mainActions} />}
			/>
			<div className="row no-margin">
				<div className="col-sm-12 padding-small">
					<VLFCard
						content={
							injury && (
								<div className="row">
									<div className="col-sm-12">
										<ViewField configuration={configuration} />
									</div>
								</div>
							)
						}
						openable={false}
					/>
				</div>
			</div>
			<VLFModal
				open={openHandleConfirm}
				title="Eliminazione"
				content={
					<div className="mt-2">
						{isInjury ? "Eliminare l'infortunio?" : "Eliminare la malattia?"}
					</div>
				}
				confirm={deleteInjury}
				close={() => setOpenHandleConfirm(false)}
				confirmLabel="Elimina"
			/>
		</Page>
	);
}

export default InjuryPage;
